import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { GET_TRANSACTION_REPORT_API } from '../../../Utils/appConstants';
import { getExportFormat, getUTCToLocalDateTimeFormat, toDayDate, queryDate } from '../../../Utils/function';
import { getRequest } from '../../../Utils/AppApi';
import { matmModeStatus, matmTransStatus, matmModeStatusDrop, matmTranStatusDrop } from '../../../Utils/Common';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Select, Tag } from 'antd';
import {
    FilterOutlined,
    SearchOutlined,
    ClearOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import PropagateLoader from "react-spinners/PropagateLoader";

/* ============================================================
   MODERN STYLES (scoped)
============================================================ */
const reportStyles = `
.aeps-page {
    padding: 16px 24px 40px;
    background: #f5f7fb;
    min-height: calc(100vh - 60px);
}
.aeps-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    flex-wrap: wrap;
    gap: 12px;
}
.aeps-page-title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2a44;
    margin: 0;
    letter-spacing: 0.2px;
}
.aeps-page-title span { color: #e5484d; }
.aeps-page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 2px 0 0;
}

.aeps-card {
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06);
    border: 1px solid #eef0f4;
    overflow: hidden;
}
.aeps-card + .aeps-card { margin-top: 18px; }

.aeps-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f2f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}
.aeps-card-head-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
    text-transform: uppercase;
    letter-spacing: .6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}
.aeps-card-head-title .anticon {
    color: #5851D2;
    font-size: 14px;
}

.aeps-filter-body { padding: 18px 20px 20px; }

.aeps-filter-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px 16px;
    align-items: end;
}

.aeps-field { display: flex; flex-direction: column; min-width: 0; }
.aeps-field label {
    font-size: 11.5px;
    font-weight: 600;
    color: #667085;
    text-transform: uppercase;
    letter-spacing: .4px;
    margin-bottom: 6px;
}

.aeps-field .ant-picker,
.aeps-field .ant-select,
.aeps-field .aeps-input {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e4e7ec;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
    font-size: 13.5px;
}
.aeps-field .ant-picker { padding: 0 11px; }
.aeps-field .ant-picker input { font-size: 13.5px; }
.aeps-field .ant-select .ant-select-selector {
    height: 40px !important;
    border-radius: 10px !important;
    border: 1px solid #e4e7ec !important;
    padding: 0 11px !important;
}
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-item,
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 13.5px;
}
.aeps-field .ant-picker:hover,
.aeps-field .ant-select:hover .ant-select-selector,
.aeps-field .aeps-input:hover { border-color: #c7cbd3 !important; }
.aeps-field .ant-picker-focused,
.aeps-field .ant-select-focused .ant-select-selector,
.aeps-field .aeps-input:focus {
    border-color: #5851D2 !important;
    box-shadow: 0 0 0 3px rgba(88,81,210,.12) !important;
    outline: none;
}

.aeps-input {
    padding: 0 12px;
    color: #101828;
    outline: none;
}
.aeps-input::placeholder { color: #98a2b3; }

/* ══════════════════════════════════════════════════════════════
   ROW 2 — Status LEFT · growing spacer · action buttons RIGHT
   No justify-content → defaults to flex-start, keeping Status
   pinned to the parent's left edge.
   ══════════════════════════════════════════════════════════════ */
.aeps-filter-grid .aeps-actions {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
    min-height: 40px;
}

/* Fixed-width Status dropdown, left-anchored */
.aeps-filter-grid .aeps-actions .aeps-actions-status {
    width: 260px;
    min-width: 220px;
}

/* ⭐ Growing spacer — pushes buttons to the right edge */
.aeps-actions-spacer {
    flex: 1 1 auto;
    min-width: 20px;
    height: 40px;
    pointer-events: none;
}

.aeps-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform .08s, box-shadow .15s, background .15s, border-color .15s;
    white-space: nowrap;
}
.aeps-btn:active { transform: translateY(1px); }

.aeps-btn-primary {
    background: linear-gradient(180deg, #6b64e0 0%, #5851D2 100%);
    color: #fff;
    box-shadow: 0 2px 6px rgba(88,81,210,.35);
}
.aeps-btn-primary:hover { box-shadow: 0 4px 12px rgba(88,81,210,.45); }

.aeps-btn-ghost {
    background: #ffffff;
    color: #475467;
    border-color: #e4e7ec;
}
.aeps-btn-ghost:hover { background: #f9fafb; border-color: #cfd4dc; }

.aeps-btn-success {
    background: #ffffff;
    color: #0e9384;
    border-color: #b7e4dd;
}
.aeps-btn-success:hover { background: #f0fdfa; border-color: #0e9384; }

.aeps-table-wrap { padding: 4px 6px 10px; }
.aeps-table-wrap .rdt_TableHeadRow {
    background: #f9fafb !important;
    border-bottom: 1px solid #eef0f4 !important;
    min-height: 46px !important;
}
.aeps-table-wrap .rdt_TableCol {
    font-size: 11.5px !important;
    font-weight: 700 !important;
    color: #475467 !important;
    text-transform: uppercase !important;
    letter-spacing: .4px !important;
}
.aeps-table-wrap .rdt_TableRow {
    border-bottom: 1px solid #f2f4f7 !important;
    min-height: 52px !important;
    transition: background .15s;
}
.aeps-table-wrap .rdt_TableRow:hover { background: #fafbff !important; }
.aeps-table-wrap .rdt_TableCell { font-size: 13px !important; color: #344054 !important; }
.aeps-table-wrap .rdt_Pagination {
    border-top: 1px solid #eef0f4 !important;
    background: #fff !important;
    min-height: 52px !important;
}

@media (max-width: 1023px) {
    .aeps-filter-grid {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    .aeps-filter-grid .aeps-actions {
        grid-column: 1 / -1;
        margin-top: 6px;
        padding-top: 16px;
        border-top: 1px dashed #eef0f4;
    }
    /* Status takes its own row on tablet/mobile */
    .aeps-filter-grid .aeps-actions .aeps-actions-status {
        width: 100%;
        min-width: 0;
    }
    .aeps-actions-spacer { display: none; }
}

@media (max-width: 640px) {
    .aeps-page { padding: 12px; }
    .aeps-filter-grid .aeps-actions { justify-content: center; }
    .aeps-filter-grid .aeps-actions .aeps-btn {
        flex: 1 1 auto;
        justify-content: center;
    }
}
`;

/* ============================================================ */

function MatmTransactionReport(props) {

    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    const [searchUserId, setSearchUserId] = useState('');
    const [searchTransId, setSearchTransId] = useState('');
    const [endDate, setEndDate] = useState(toDayDate());
    const [startDate, setStartDate] = useState(toDayDate());
    const [getData, setData] = useState({ docs: [] });
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(limit);
    const [modeType, setModeType] = useState('');
    const [transStatus, setTransStatus] = useState('');
    const [isLoading, setLoading] = useState(false);

    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [tableResetKey, setTableResetKey] = useState(0);

    // ============================================================
    //  COLUMNS
    // ============================================================
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "200px"
        },
        {
            name: 'User ID',
            selector: row => <Tooltip placement="bottom" title={row.users?.username}>{row.users?.username}</Tooltip>,
            width: "110px"
        },
        {
            name: 'Mode',
            selector: row => matmModeStatus(row.typeOfTransaction),
            width: "180px"
        },
        {
            name: 'API Provider',
            cell: () => (
                <Tooltip placement="bottom" title="FingPay">
                    <Tag
                        color="blue"
                        style={{ fontSize: '11px', fontWeight: 600, margin: 0 }}
                    >
                        FP
                    </Tag>
                </Tooltip>
            ),
            width: "120px",
            center: true,
        },
        {
            name: 'Bank RRN',
            selector: row => <Tooltip placement="bottom" title={row.bankRRN}>{row.bankRRN}</Tooltip>,
            width: "150px"
        },
        {
            name: 'Mobile',
            selector: row => <Tooltip placement="bottom" title={row.mobile}>{row.mobile}</Tooltip>,
            width: "120px"
        },
        {
            name: 'Card Number',
            selector: row => <Tooltip placement="bottom" title={row.cardNumber}>{row.cardNumber}</Tooltip>,
            width: "150px"
        },
        {
            name: 'Amount',
            selector: row => row.amount,
            width: "100px"
        },
        {
            name: 'Transaction Id',
            selector: row => <Tooltip placement="bottom" title={row.transactionId}>{row.transactionId}</Tooltip>,
            width: "180px"
        },
        {
            name: 'Status',
            selector: row => matmTransStatus(row.transactionStatus),
            width: "110px"
        },
        {
            name: 'Terminal Id',
            selector: row => <Tooltip placement="bottom" title={row.terminalId}>{row.terminalId}</Tooltip>,
            width: "120px"
        },
        {
            name: 'Message',
            selector: row => <Tooltip placement="bottom" title={row.message}>{row.message}</Tooltip>,
            width: "180px"
        }
    ];

    // ============================================================
    //  HELPERS
    // ============================================================
    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    // ============================================================
    //  FETCH
    // ============================================================
    const getDataReports = () => {
        setLoading(true);
        const params = { page, limit: isLimit };

        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId || "";
        params.searchTransId = searchTransId || "";
        params.mode = modeType;
        params.status = transStatus;
        params.isAdmin = true;

        const queryString = objectToQueryString(params);
        getRequest(`${GET_TRANSACTION_REPORT_API}?${queryString}`, history)
            .then((response) => {
                if (response.success === true && response.data) {
                    setData(response.data);
                    setTotalRecord(response.data.totalDocs || 0);
                    if (typeof response.data.page === 'number') setPage(response.data.page);
                } else {
                    toast.error(response.message || 'Failed to fetch data');
                    setData({ docs: [] });
                    setTotalRecord(0);
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error('Network error or server error');
                setData({ docs: [] });
                setTotalRecord(0);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getDataReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTrigger]);

    // ============================================================
    //  CSV
    // ============================================================
    const getCSVDataList = () => {
        const params = { exportsCsv: true };
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId || "";
        params.searchTransId = searchTransId || "";
        params.mode = modeType;
        params.status = transStatus;
        params.isAdmin = true;

        const queryString = objectToQueryString(params);
        getRequest(`${GET_TRANSACTION_REPORT_API}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    const csvdataRecord = (response.data || []).map(e => ({
                        "Date Time": getExportFormat(e.createdAt),
                        "User ID": e.users?.username || '',
                        "Mode": matmModeStatus(e.typeOfTransaction),
                        "API Provider": "FP",
                        "Bank RRN": e.bankRRN,
                        "Mobile": e.mobile,
                        "Card Number": e.cardNumber,
                        "Amount": e.amount,
                        "Transaction Id": e.transactionId,
                        "Status": matmTransStatus(e.transactionStatus),
                        "Terminal Id": e.terminalId,
                        "Message": e.message,
                    }));
                    setCsvData(csvdataRecord);
                    setTimeout(() => {
                        if (csvLink.current && csvLink.current.link) {
                            csvLink.current.link.click();
                        }
                    }, 500);
                } else {
                    toast.error(response.message || 'Failed to export data');
                    setCsvData([]);
                }
            })
            .catch((error) => {
                console.error(error);
                toast.error('Network error while exporting');
                setCsvData([]);
            });
    };

    // ============================================================
    //  HANDLERS
    // ============================================================
    const resetToFirstPageAndFetch = () => {
        setPage(1);
        setTableResetKey((k) => k + 1);
        setFetchTrigger((k) => k + 1);
    };

    const handlePageChange = (newPage /*, size */) => {
        setPage(newPage);
        setFetchTrigger((k) => k + 1);
    };

    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        resetToFirstPageAndFetch();
    };

    const handleSearch = () => resetToFirstPageAndFetch();

    const handleClear = () => {
        setStartDate(toDayDate());
        setEndDate(toDayDate());
        setModeType('');
        setTransStatus('');
        setSearchTransId('');
        setSearchUserId('');
        resetToFirstPageAndFetch();
    };

    const onChangeStartDate = (date) => setStartDate(date);
    const onChangeEndDate = (date) => setEndDate(date);
    const onChangeModeType = (value) => setModeType(value);
    const onChangeTranStatus = (value) => setTransStatus(value);

    // ============================================================
    //  RENDER
    // ============================================================
    return (
        <div className="aeps-page">
            <style>{reportStyles}</style>

            {/* <div className="aeps-page-header">
                <div>
                    <h1 className="aeps-page-title">
                        Matm <span>Transaction Report</span>
                    </h1>
                    <p className="aeps-page-subtitle">
                        Search, filter, and export MATM transaction records
                    </p>
                </div>
            </div> */}

            <div className="aeps-card">
                {/* <div className="aeps-card-head">
                    <h2 className="aeps-card-head-title">
                        <FilterOutlined /> Filters
                    </h2>
                </div> */}

                <div className="aeps-filter-body">
                    <div className="aeps-filter-grid">
                        {/* ════════ ROW 1 — 5 fields (one empty col) ════════ */}
                        <div className="aeps-field">
                            <label>From Date</label>
                            <DatePicker
                                value={startDate}
                                format={dateFormat}
                                onChange={onChangeStartDate}
                                placeholder="From date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>To Date</label>
                            <DatePicker
                                value={endDate}
                                format={dateFormat}
                                onChange={onChangeEndDate}
                                placeholder="To date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>User ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter User ID"
                                onChange={(e) => setSearchUserId(e.target.value)}
                                value={searchUserId}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Trans ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Trans ID"
                                onChange={(e) => setSearchTransId(e.target.value)}
                                value={searchTransId}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Mode</label>
                            <Select
                                options={matmModeStatusDrop}
                                value={modeType || undefined}
                                className="normal-light"
                                classNamePrefix="select"
                                placeholder="Select mode"
                                onChange={onChangeModeType}
                                allowClear
                            />
                        </div>

                        {/* ════════ ROW 2 — Status LEFT · spacer · buttons RIGHT ════════ */}
                        <div className="aeps-actions">
                            <div className="aeps-field aeps-actions-status">
                                <label>Status</label>
                                <Select
                                    options={matmTranStatusDrop}
                                    value={transStatus || undefined}
                                    className="normal-light"
                                    classNamePrefix="select"
                                    placeholder="Select status"
                                    onChange={onChangeTranStatus}
                                    allowClear
                                />
                            </div>

                            {/* ⭐ Growing spacer — pushes buttons to the right edge */}
                            <div className="aeps-actions-spacer" aria-hidden="true" />

                            <button type="button" className="aeps-btn aeps-btn-ghost" onClick={handleClear}>
                                <ClearOutlined /> Clear
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-success" onClick={getCSVDataList}>
                                <DownloadOutlined /> Export CSV
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-primary" onClick={handleSearch}>
                                <SearchOutlined /> Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="aeps-card">
                <div className="aeps-table-wrap">
                    <DataTable
                        key={tableResetKey}
                        title=""
                        columns={columns}
                        data={getData.docs || []}
                        highlightOnHover
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        paginationDefaultPage={page}
                        paginationPerPage={isLimit}
                        progressPending={isLoading}
                        progressComponent={
                            <div className="text-center py-4">
                                <PropagateLoader color="#5851D2" size={20} />
                            </div>
                        }
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        noDataComponent={
                            <div style={{ padding: '40px 20px', color: '#98a2b3', fontSize: 13 }}>
                                No records found for the selected filters.
                            </div>
                        }
                    />
                </div>
            </div>

            <CSVLink
                data={getCsvData}
                filename="matm-transaction-report"
                className="hidden"
                ref={csvLink}
                target="_blank"
                style={{ display: 'none' }}
            />
        </div>
    );
}

export default MatmTransactionReport;